import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useRealisations, resolveImage } from "../lib/realisations";
import { heroCardStyle } from "../lib/heroCard";
import { useSiteSettings } from "../lib/settings";
import { useContent } from "../lib/content";
import { Icon } from "../lib/icons";
import { ContactBlock } from "../components/ContactBlock";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";

export default function Home() {
  const { list: realisations } = useRealisations();
  const { settings } = useSiteSettings();
  const c = useContent("home");
  const featured = (realisations || []).filter((r) => r.featured).slice(0, 3);
  const featuredOrFallback = featured.length > 0 ? featured : (realisations || []).slice(0, 3);
  const heroImage = settings?.hero_image || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=2400&q=85";
  const qualifications = settings?.qualifications || [];
  const expertises = c.expertises || [];
  const values = c.values || [];

  return (
    <div data-testid="page-home">
      <SEO path="/" />
      {/* HERO — modern, premium, minimal */}
      <section className="relative h-[calc(100vh-6rem)] lg:h-[calc(100vh-7rem)] min-h-[600px] overflow-hidden bg-[#1A1A1A] flex items-center justify-center">
        <img
          src={resolveImage(heroImage)}
          alt="Bâtiment moderne en construction"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: ((c.hero_image_opacity ?? 100) / 100) }}
          fetchPriority="high"
        />
        {/* Aucune surcouche sombre — image en pleine luminosité (100%) */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent pointer-events-none" />

        <div className="relative w-full max-w-7xl mx-auto px-6 lg:px-8 py-10">
          <div
            className="w-full max-w-4xl mx-auto text-center border border-white/15 rounded-2xl px-8 py-12 md:px-12 md:py-14 shadow-2xl shadow-black/20"
            style={heroCardStyle(c)}
          >

            {/* TITRE */}
            <h1
              className="font-display font-bold uppercase leading-[0.95] tracking-[0.01em] text-5xl sm:text-6xl md:text-7xl lg:text-8xl animate-fade-up"
              style={{ animationDelay: "0ms", animationFillMode: "both" }}
              data-testid="hero-title"
            >
              <span className="block text-white">{c.hero_title_line1 || "ECO-GEST"}</span>
              <span className="block text-[#C8102E]">{c.hero_title_line2 || "PATRIMOINE"}</span>
            </h1>

            {/* SLOGAN principal — centré, sans point final (suite avec le badge) */}
            <p
              className="mt-8 text-xl md:text-2xl lg:text-[1.6rem] font-medium text-white tracking-tight leading-snug max-w-3xl mx-auto animate-fade-up"
              style={{ animationDelay: "200ms", animationFillMode: "both" }}
              data-testid="hero-slogan"
            >
              Construisons et gérons ensemble l&apos;avenir
            </p>

            {/* BADGE 25 ANS — juste sous le slogan, suite de la phrase */}
            <div
              className="mt-4 flex justify-center animate-fade-up"
              style={{ animationDelay: "300ms", animationFillMode: "both" }}
            >
              <div
                data-testid="hero-badge-25"
                className="inline-flex items-center gap-2.5 bg-[#C8102E] text-white px-5 py-2.5 rounded-lg shadow-lg shadow-black/40 text-xs md:text-sm font-bold tracking-[0.18em] uppercase relative"
              >
                <span className="relative flex items-center justify-center">
                  <span className="inline-block w-2 h-2 bg-white rounded-full animate-party-sparkle" />
                  <span className="absolute -top-1 -right-1 w-1 h-1 bg-yellow-300 rounded-full animate-party-sparkle" style={{ animationDelay: "0.5s" }} />
                </span>
                Depuis plus de 25 ans
                <span className="relative flex items-center justify-center">
                  <span className="inline-block w-2 h-2 bg-white rounded-full animate-party-sparkle" style={{ animationDelay: "0.7s" }} />
                  <span className="absolute -top-1 -left-1 w-1 h-1 bg-yellow-300 rounded-full animate-party-sparkle" style={{ animationDelay: "1.1s" }} />
                </span>
              </div>
            </div>

            {/* DESCRIPTION — blanc 85% */}
            <p
              className="mt-5 text-sm md:text-base leading-relaxed max-w-2xl mx-auto animate-fade-up"
              style={{ animationDelay: "400ms", animationFillMode: "both", color: "rgba(255,255,255,0.85)" }}
              data-testid="hero-description"
            >
              {c.hero_description || "Un accompagnement rigoureux et personnalisé pour la réussite de vos projets de construction."}
            </p>

            {/* MÉTIERS — centrés, dots ronds */}
            <ul
              className="mt-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-3 text-base md:text-lg lg:text-xl font-semibold tracking-wide text-white animate-fade-up"
              style={{ animationDelay: "500ms", animationFillMode: "both" }}
              data-testid="hero-metiers"
            >
              {(c.hero_bullets || []).map((b, i) => (
                <li key={i} className="flex items-center gap-4">
                  {i > 0 && <span className="inline-block w-2.5 h-2.5 bg-[#C8102E] rounded-full shrink-0" aria-hidden="true" />}
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            {/* CTA BUTTONS — 56px de haut, rayon 8px */}
            <div
              className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4 justify-center animate-fade-up"
              style={{ animationDelay: "650ms", animationFillMode: "both" }}
            >
              <Link
                to="/realisations"
                data-testid="hero-cta-realisations"
                className="group inline-flex items-center justify-center gap-3 bg-[#C8102E] hover:bg-[#A50C24] text-white px-8 text-sm font-semibold tracking-wide uppercase shadow-lg shadow-black/30 hover:shadow-xl hover:-translate-y-0.5 transition-[transform,background-color,box-shadow] duration-300"
                style={{ height: "56px", borderRadius: "8px" }}
              >
                {c.hero_cta_primary || "Découvrir nos réalisations"}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" strokeWidth={2} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="inline-flex items-center justify-center gap-3 border border-white/60 hover:border-white text-white hover:bg-white hover:text-[#1A1A1A] px-8 text-sm font-semibold tracking-wide uppercase shadow-lg shadow-black/20 hover:shadow-xl hover:-translate-y-0.5 transition-[transform,background-color,color,border-color,box-shadow] duration-300"
                style={{ height: "56px", borderRadius: "8px" }}
              >
                {c.hero_cta_secondary || "Nous contacter"}
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-[10px] tracking-[0.3em] uppercase animate-bounce hidden md:block">
          {c.hero_scroll_hint}
        </div>
      </section>

      {/* EXPERTISES */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="text-center mb-14 max-w-3xl mx-auto">
            <span className="overline text-[#D71920] text-xs font-bold tracking-[0.3em]">— Notre savoir-faire</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold tracking-tight text-[#2F3136]">{c.expertises_title}</h2>
            <div className="w-12 h-0.5 bg-[#D71920] mx-auto mt-6" />
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {expertises.map((exp, i) => (
              <Reveal as="div" delay={i * 150} key={exp.key || i} data-testid={`expertise-card-${exp.key || i}`} className="group relative">
                <div className="relative h-full p-8 lg:p-10 bg-white border border-gray-200 hover:border-[#D71920] hover:shadow-xl hover:-translate-y-1 transition-[border-color,box-shadow,transform] duration-500">
                  {/* Big number in background */}
                  <span aria-hidden="true" className="absolute top-4 right-6 font-display text-7xl lg:text-8xl font-black text-gray-100 group-hover:text-[#D71920]/10 transition-colors duration-500 select-none leading-none">
                    {exp.number}
                  </span>
                  <Icon name={exp.icon} className="relative w-12 h-12 text-[#2F3136] group-hover:text-[#D71920] group-hover:scale-110 transition-[color,transform] duration-500" strokeWidth={1.25} />
                  <h3 className="relative mt-8 font-display text-lg md:text-xl font-bold tracking-tight text-[#2F3136]">{exp.title}</h3>
                  <div className="relative w-8 h-0.5 bg-[#D71920] mt-3 mb-6 group-hover:w-16 transition-[width] duration-500" />
                  <ul className="relative space-y-2.5">
                    {(exp.items || []).map((item, j) => (
                      <li key={j} className="flex items-start gap-3 text-[#2F3136]/80 text-sm">
                        <span className="mt-1.5 inline-block w-1.5 h-1.5 bg-[#D71920] shrink-0 rounded-full" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={exp.link || "/metiers"} data-testid={`expertise-link-${exp.key || i}`} className="relative mt-8 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#2F3136] hover:text-[#D71920] transition-colors">
                    {exp.link_label || "En savoir plus"}
                    <ArrowUpRight className="w-3.5 h-3.5 text-[#D71920] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" strokeWidth={2} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 md:py-24 bg-[#F7F7F8] border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="text-center mb-14">
            <span className="overline text-[#D71920] text-xs font-bold tracking-[0.3em]">— Nos valeurs</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold tracking-tight text-[#2F3136]">{c.values_title}</h2>
            <div className="w-12 h-0.5 bg-[#D71920] mx-auto mt-6" />
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
            {values.map((v, i) => (
              <Reveal key={i} delay={i * 120} className="text-center">
                <div data-testid={`value-${(v.title || "v").toLowerCase().replace(/\s/g, "")}`} className="group">
                  <div className="mx-auto w-16 h-16 rounded-full border border-gray-300 flex items-center justify-center mb-5 group-hover:border-[#D71920] group-hover:scale-110 transition-[border-color,transform] duration-500">
                    <Icon name={v.icon} className="w-7 h-7 text-[#2F3136] group-hover:text-[#D71920] transition-colors duration-500" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-base font-semibold tracking-tight text-[#2F3136] mb-1.5">{v.title}</h3>
                  <p className="text-xs text-[#2F3136]/70 leading-relaxed max-w-[180px] mx-auto">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SEO TEXT BLOCK — éditable depuis /admin/contenu */}
      {(c.seo_title || (c.seo_sections || []).length > 0) && (
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 prose prose-sm md:prose-base prose-neutral">
            {c.seo_title && (
              <Reveal>
                <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-6">
                  {c.seo_title}
                </h2>
              </Reveal>
            )}
            {(c.seo_sections || []).map((s, i) => (
              <Reveal key={i} delay={Math.min(i * 60, 240)}>
                {s.title && s.level !== "intro" && (
                  <h3 className="font-display text-lg md:text-xl font-bold mt-8 mb-3">{s.title}</h3>
                )}
                {s.body && (
                  <p className="text-gray-700 leading-relaxed mb-4 whitespace-pre-line">{s.body}</p>
                )}
              </Reveal>
            ))}
            <Reveal>
              <p className="text-sm text-gray-500 mt-6">
                <Link to="/realisations" className="text-[#E60000] underline-offset-2 hover:underline">Découvrir l&apos;ensemble de nos réalisations</Link>
                {" · "}
                <Link to="/contact" className="text-[#E60000] underline-offset-2 hover:underline">Nous contacter</Link>
              </p>
            </Reveal>
          </div>
        </section>
      )}

      {/* FEATURED PROJECTS */}
      <section className="py-20 md:py-28 bg-[#2F3136] text-white relative overflow-hidden">
        {/* Subtle red glow accent */}
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#D71920]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="text-center mb-14">
            <span className="overline text-[#D71920] text-xs font-bold tracking-[0.3em]">— Réalisations</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold tracking-tight">{c.featured_title}</h2>
            <div className="w-12 h-0.5 bg-[#D71920] mx-auto mt-6" />
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {featuredOrFallback.map((p, i) => (
              <Reveal key={p.id} delay={i * 150}>
                <Link to={`/realisations/${p.slug || p.id}`} data-testid={`featured-project-${p.slug || p.id}`} className="group block bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-[#D71920]/60 transition-[background-color,border-color,transform] duration-500 hover:-translate-y-1">
                  <div className="aspect-[4/3] overflow-hidden bg-gray-800 relative">
                    <img loading="lazy" src={resolveImage(p.cover)} alt={p.name} className="img-cover group-hover:scale-[1.08] transition-transform duration-[1400ms]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute top-4 left-4 bg-[#D71920] text-white text-[10px] font-bold tracking-[0.18em] uppercase px-2.5 py-1 opacity-0 group-hover:opacity-100 -translate-y-2 group-hover:translate-y-0 transition-[opacity,transform] duration-500">
                      Découvrir
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-base lg:text-lg font-semibold tracking-tight mb-1 group-hover:text-[#D71920] transition-colors duration-300">{p.name}</h3>
                    <p className="text-xs text-white/60 tracking-wide uppercase">Mission · {p.type}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-14">
            <Link to="/realisations" data-testid="home-link-all-realisations" className="inline-flex items-center gap-3 bg-[#D71920] hover:bg-[#B30000] text-white px-8 py-4 rounded-lg text-sm font-semibold tracking-wide uppercase shadow-lg shadow-black/30 hover:shadow-xl hover:-translate-y-0.5 transition-[transform,background-color,box-shadow] duration-300">
              {c.featured_cta}
              <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>

      {/* QUALIFICATIONS */}
      <section className="py-16 md:py-20 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <span className="overline text-[#D71920] text-xs font-bold tracking-[0.3em]">— Certifications</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold tracking-tight text-[#2F3136]">{c.qualifications_title}</h2>
            <div className="w-12 h-0.5 bg-[#D71920] mx-auto mt-6" />
          </Reveal>
          <Reveal delay={150}>
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
              {qualifications.map((q, i) => (
                <div key={i} data-testid={`qualification-${(q.name || "q").toLowerCase()}`} className="px-8 py-6 border border-gray-200 min-w-[160px] text-center hover:border-[#D71920] hover:shadow-md hover:-translate-y-0.5 transition-[border-color,box-shadow,transform] duration-500">
                  {q.logo ? (
                    <img src={resolveImage(q.logo)} alt={q.name} className="h-12 w-auto mx-auto object-contain" loading="lazy" />
                  ) : (
                    <div className="font-display text-2xl md:text-3xl font-bold tracking-tight" style={{ color: q.color || "#2F3136" }}>
                      {q.name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <ContactBlock />
    </div>
  );
}
